import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth-store';
import type { SystemRoleKey, UserType } from '@/types/auth';

interface RoleGuardProps {
  /**
   * UserType allow-list. When omitted, every userType passes the
   * userType check (only `denyRoles` will gate access).
   */
  allow?: ReadonlyArray<UserType>;
  /**
   * Fine-grained tenant role allow-list. When `allow` and
   * `allowRoles` are both supplied, matching either one grants the
   * route before deny-list checks run.
   */
  allowRoles?: ReadonlyArray<SystemRoleKey>;
  /**
   * Role-key deny-list. Any user whose `roleKeys` overlaps this set
   * is redirected to `fallback`. Used to block surfaces that are
   * technically reachable but semantically wrong for the role —
   * e.g. Tenant Admin opening the Executive dashboard.
   */
  denyRoles?: ReadonlyArray<SystemRoleKey>;
  children: ReactNode;
  /**
   * Where to send a user whose role doesn't match. Defaults to "/"
   * because every authenticated user has a home route defined there.
   */
  fallback?: string;
}

/**
 * Hard role gate at the route level. The backend enforces the same
 * checks via @RequirePolicy() — this guard exists to avoid rendering
 * pages the user can't use, not as the source of truth.
 */
export function RoleGuard({
  allow,
  allowRoles,
  denyRoles,
  children,
  fallback = '/',
}: RoleGuardProps): JSX.Element {
  const { user } = useAuthStore();
  if (!user) {
    return <Navigate to={fallback} replace />;
  }
  const userRoles = user.roleKeys ?? [];
  const hasAllowedType = allow ? allow.includes(user.userType) : false;
  const hasAllowedRole = allowRoles
    ? userRoles.some((r) => allowRoles.includes(r as SystemRoleKey))
    : false;

  if ((allow || allowRoles) && !hasAllowedType && !hasAllowedRole) {
    return <Navigate to={fallback} replace />;
  }
  if (denyRoles && denyRoles.length > 0) {
    if (userRoles.some((r) => denyRoles.includes(r as SystemRoleKey))) {
      return <Navigate to={fallback} replace />;
    }
  }
  return <>{children}</>;
}
